import type { AlertRecipient, SiteListItem } from '@tabel/shared/schemas/sites';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, MapPin, Smartphone, X } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../../../components/ErrorToast';
import { Button, Card, EmptyState, IconButton, PageHeader, Skeleton, inputCls } from '../../../components/ui';
import { useSetCrumbs } from '../../../lib/crumbs';
import { reportClientError } from '../../../lib/errors';
import { SETTINGS_PATH } from '../../../lib/nav';
import { useRole } from '../../../lib/role';
import { type RecipientRow, putAlertRecipients, siteAlerts, sitesKeys, sitesList } from '../api';
import { settingsCrumbs } from '../crumbs';
import { FormField, LoadError, Refused, SCREEN, isForbidden, shouldReport } from '../parts';
import { EXAMPLES, RECIPIENTS } from '../texts';

/*
 * Раздел «Тревоги площадок» (007 T047; группа «Уведомления»; spec.md FR-051, FR-052, «Права ролей»; data-model А.9;
 * contracts/http-api.md, «Тревоги и карантин», уточнения T045; site-onboarding-design.md разделы 2, 6; ux.md §11).
 * Раздел-форма: по блоку на каждую площадку, где у пользователя роль администратора системы (`GET /api/sites`). В
 * блоке — подзаголовок с названием площадки и ссылкой на её страницу, список получателей из `GET …/alerts`
 * (`SiteAlertsView`; рамку получает только список) и строка «добавить» — тема ntfy (сообщение на телефон через наш
 * сервер уведомлений). Настройка действует для всех и применяется сразу: «Добавить получателя» и «Убрать» сохраняют
 * весь список (`PUT …/alert-recipients`, ответ — тот же `SiteAlertsView`), тост «Отменить». Права проверяет сервер
 * (`sites.alerts`).
 */

/** Тема ntfy: латиница, цифры, «-» и «_», до 64 знаков (правило сервера ntfy). */
const NTFY_TOPIC = /^[A-Za-z0-9_-]{1,64}$/;
/** Получателей у площадки — не больше (`AlertRecipientsPut`, схема `@tabel/shared/schemas/sites`). */
const MAX_RECIPIENTS = 50;

export function AlertsSection() {
  useSetCrumbs(settingsCrumbs({ label: RECIPIENTS.title }));
  const { role } = useRole();
  const q = useQuery({ queryKey: sitesKeys.list(role), queryFn: sitesList });
  useEffect(() => {
    if (q.error && shouldReport(q.error)) reportClientError(SCREEN, 'alerts-sites', q.error);
  }, [q.error]);

  let body: ReactNode;
  if (q.isError)
    body = isForbidden(q.error) ? (
      <Refused title={RECIPIENTS.forbiddenTitle} text={RECIPIENTS.forbidden} error={q.error} />
    ) : (
      <LoadError
        title={RECIPIENTS.sitesError}
        error={q.error}
        busy={q.isFetching}
        onRetry={() => void q.refetch()}
      />
    );
  else if (!q.data) body = <Loading />;
  else if (q.data.length === 0)
    body = (
      <Card>
        <EmptyState icon={BellRing} title={RECIPIENTS.emptyTitle} description={RECIPIENTS.emptyText} />
      </Card>
    );
  else body = q.data.map((s) => <SiteRecipients key={s.code} site={s} />);

  return (
    <>
      <PageHeader title={RECIPIENTS.title} description={RECIPIENTS.description} />
      <div className="mt-2">{body}</div>
    </>
  );
}

function Loading() {
  return (
    <Card className="mt-6 space-y-3 p-4" aria-busy="true" aria-label="Загрузка…">
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="h-5 w-2/3" />
    </Card>
  );
}

/** Получатели одной площадки: список, «Убрать» с «Отменить», строка «добавить». */
function SiteRecipients({ site }: { site: SiteListItem }) {
  const id = useId();
  const { role } = useRole();
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const key = sitesKeys.alerts(role, site.code);
  const q = useQuery({ queryKey: key, queryFn: () => siteAlerts(site.code) });
  useEffect(() => {
    if (q.error && shouldReport(q.error)) reportClientError(SCREEN, 'alert-recipients', q.error);
  }, [q.error]);
  const [busy, setBusy] = useState(false);
  const recipients = q.data?.recipients;

  /**
   * Сохранить весь список; получилось — `true`, список — из ответа сервера (`SiteAlertsView`). Ошибка — тост с кодом
   * ошибки, список перечитывается.
   */
  const save = async (next: AlertRecipient[], action: string): Promise<boolean> => {
    setBusy(true);
    try {
      qc.setQueryData(key, await putAlertRecipients(site.code, next));
      return true;
    } catch (err) {
      showError(err, { screen: SCREEN, action });
      void qc.invalidateQueries({ queryKey: key });
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Весь список, и «уведомление в сервисе», заданное раньше: `PUT` заменяет список целиком.
  const current: AlertRecipient[] = (recipients ?? []).map((r) => ({ channel: r.channel, target: r.target }));
  const remove = async (r: RecipientRow) => {
    const before = current;
    const next = current.filter((x) => !(x.channel === r.channel && x.target === r.target));
    if (!(await save(next, 'alert-recipients-remove'))) return;
    showInfo(RECIPIENTS.removed, {
      action: { label: RECIPIENTS.undo, onClick: () => void save(before, 'alert-recipients-undo') },
    });
  };

  let list: ReactNode;
  if (q.isError)
    list = (
      <LoadError
        title={RECIPIENTS.loadError}
        error={q.error}
        busy={q.isFetching}
        onRetry={() => void q.refetch()}
      />
    );
  else if (!recipients)
    list = (
      <div className="space-y-2" aria-busy="true" aria-label="Загрузка…">
        <Skeleton className="h-5 w-1/2" />
      </div>
    );
  else if (recipients.length === 0) list = <p className="text-[13px] text-ink-2">{RECIPIENTS.empty}</p>;
  else
    list = (
      <ul aria-label={RECIPIENTS.listLabel(site.name)} className="rounded-control border border-line">
        {recipients.map((r) => {
          const who = r.channel === 'ntfy' ? r.target : RECIPIENTS.user(r.target);
          const Icon = r.channel === 'ntfy' ? Smartphone : BellRing;
          return (
            <li
              key={`${r.channel}-${r.target}`}
              className="flex items-center gap-3 border-b border-line px-3 py-2 last:border-b-0"
            >
              <Icon size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="text-[14px] break-all text-ink">{who}</div>
                <div className="text-[12px] text-ink-2">{RECIPIENTS.channels[r.channel]}</div>
              </div>
              <IconButton
                icon={X}
                label={RECIPIENTS.remove(who)}
                disabled={busy}
                onClick={() => void remove(r)}
              />
            </li>
          );
        })}
      </ul>
    );

  return (
    <section aria-labelledby={`${id}-h`}>
      <div className="t-set-sub flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 id={`${id}-h`} className="text-[14px] font-semibold text-ink">
          {site.name}
        </h2>
        <Link
          to={SETTINGS_PATH.site(site.code)}
          className="inline-flex items-center gap-1 text-[13px] font-medium text-accent hover:underline max-sm:min-h-10"
        >
          <MapPin size={14} strokeWidth={1.75} aria-hidden />
          {RECIPIENTS.toSite}
        </Link>
      </div>
      <div className="mt-3 flex flex-col gap-3">
        {list}
        {recipients && (
          <AddRecipient
            current={current}
            busy={busy}
            onAdd={async (r) => {
              const before = current;
              if (!(await save([...current, r], 'alert-recipients-add'))) return false;
              showInfo(RECIPIENTS.added, {
                action: { label: RECIPIENTS.undo, onClick: () => void save(before, 'alert-recipients-undo') },
              });
              return true;
            }}
          />
        )}
      </div>
    </section>
  );
}

/**
 * Строка «добавить»: тема ntfy и «Добавить получателя». Проверка — при отправке (ux.md §13). Способ «уведомление в
 * сервисе» не добавляется до страницы «Пользователи и роли» (решение координатора, вопрос T047 Q1).
 */
function AddRecipient({
  current,
  busy,
  onAdd,
}: {
  current: readonly AlertRecipient[];
  busy: boolean;
  onAdd: (r: AlertRecipient) => Promise<boolean>;
}) {
  const id = useId();
  const [target, setTarget] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const value = target.trim();
    const E = RECIPIENTS.errors;
    let problem: string | null = null;
    if (!NTFY_TOPIC.test(value)) problem = E.ntfy;
    else if (current.some((r) => r.channel === 'ntfy' && r.target === value)) problem = E.duplicate;
    else if (current.length >= MAX_RECIPIENTS) problem = E.max;
    setError(problem);
    if (problem) return;
    if (await onAdd({ channel: 'ntfy', target: value })) setTarget('');
  };

  return (
    <form noValidate onSubmit={(e) => void submit(e)} className="flex flex-wrap items-start gap-3">
      <FormField
        id={`${id}-target`}
        label={RECIPIENTS.targetLabel}
        hint={RECIPIENTS.targetHint}
        error={error}
        className="min-w-[200px] flex-1 max-sm:w-full"
      >
        {(a) => (
          <input
            {...a}
            className={`${inputCls} font-mono`}
            value={target}
            autoComplete="off"
            spellCheck={false}
            placeholder={RECIPIENTS.placeholder(EXAMPLES.ntfyTopic)}
            onChange={(e) => {
              setTarget(e.target.value);
              setError(null);
            }}
          />
        )}
      </FormField>
      {/* Кнопка — на уровне полей: под подписью (13 px + 6 px), на телефоне — во всю ширину. */}
      <div className="pt-[25px] max-sm:w-full max-sm:pt-0">
        <Button type="submit" disabled={busy} className="max-sm:w-full">
          {RECIPIENTS.add}
        </Button>
      </div>
    </form>
  );
}
